<script lang="ts" setup>
import { computed } from "vue";

import date from "@/libs/date";
import { format } from "@/libs/amount.ts";

import { ArrowPathClockIcon } from "@sidekickicons/vue/24/outline";

import type { Dayjs } from "dayjs";
import type Transaction from "@/classes/Transaction.ts";

import { displayRows, onEdit } from "@/functions/transactions.ts";

const props = defineProps<{
  transactions: Transaction[];
  periods: Dayjs[];
  periodFormat: string;
}>();

const rows = computed(() => displayRows(props.transactions));

// Real amounts even when disabled (faded but still readable and editable); the parent
// row's sum() already excludes them from totals.
const amountIn = (row: Transaction[], period: Dayjs) => {
  const inPeriod = row.filter(
    (tx) => date(tx.date).format(props.periodFormat) === period.format(props.periodFormat),
  );

  return inPeriod.length === 0
    ? ""
    : format(
        inPeriod.reduce((acc, tx) => acc + tx.amount, 0),
        inPeriod[0].currency,
      );
};

// Day-of-month only: the period columns already carry month/year.
const dayLabel = (row: Transaction[]) =>
  [...new Set(row.map((tx) => date(tx.date).format("D")))].join(", ");

const isSeries = (row: Transaction[]) => row.length > 1 || row[0].recurrence !== null;
</script>

<template>
  <tr
    v-for="row in rows"
    :key="row[0].id"
    :class="[
      { 'opacity-50': row.every((tx) => tx.isDisabled) },
      'divide-x divide-gray-200 cursor-pointer hover:bg-gray-50',
    ]"
    @click="onEdit(row[0].id!, row[0].type)"
  >
    <th
      class="py-2 px-4 flex flex-row gap-x-3 items-center text-sm text-left font-normal whitespace-nowrap text-gray-500"
      scope="row"
    >
      {{ dayLabel(row) }}
      <span v-if="row[0].description" class="text-gray-400">{{ row[0].description }}</span>
      <ArrowPathClockIcon
        v-if="isSeries(row)"
        class="size-4 ml-auto shrink-0"
        :title="row[0].recurrence?.toText()"
      />
    </th>
    <td
      v-for="period in periods"
      :key="period.format(periodFormat)"
      :class="[
        row[0].type === 'expense' ? 'text-red-600' : 'text-gray-900',
        'py-2 pr-4 pl-4 text-right text-sm font-medium whitespace-nowrap sm:pl-0',
      ]"
    >
      {{ amountIn(row, period) }}
    </td>
  </tr>
</template>
